import { useEffect, useMemo, useRef, useState } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import {
  FaAddressBook,
  FaAngleDoubleLeft,
  FaBriefcase,
  FaCalculator,
  FaCalendarAlt,
  FaChartBar,
  FaCheckDouble,
  FaClipboardCheck,
  FaClipboardList,
  FaClock,
  FaCogs,
  FaComment,
  FaExternalLinkAlt,
  FaFileInvoice,
  FaFileSignature,
  FaGlobe,
  FaHome,
  FaPuzzlePiece,
  FaRegStickyNote,
  FaSearch,
  FaTshirt,
} from 'react-icons/fa'

import { logoWhiteSVG } from '../../assets'
import { getEnquiryCount } from '../../data/enquiryData'
import { getNoteCount } from '../../data/notes'
import { clearPersistedGridState } from '../../helpers/useSessionState'
import { visibleManagementOptions } from '../../pages/Management/managementOptions'
import Feedback from '../Modals/Feedback'
import packageJson from '../../../package.json'

/**
 * The portal's left navigation: every destination the user's roles allow,
 * grouped by area.
 */

interface NavItem {
  key: string
  label: string
  icon: JSX.Element
  /** A route in the portal, or an external address when external is set. */
  to?: string
  external?: boolean
  /** A button rather than a link. */
  action?: () => void
  badge?: number
  /** Going home starts again, so it forgets remembered grid filters. */
  clearsFilters?: boolean
  visible: boolean
}

interface NavGroup {
  title: string | null
  items: NavItem[]
}

// Named from when this was a preview; kept so people keep their setting.
const COLLAPSED_KEY = 'previewSidebarCollapsed'

const readCollapsed = () => {
  try {
    return window.localStorage.getItem(COLLAPSED_KEY) === '1'
  } catch {
    return false
  }
}

const writeCollapsed = (collapsed: boolean) => {
  try {
    window.localStorage.setItem(COLLAPSED_KEY, collapsed ? '1' : '0')
  } catch {
    // Not remembering is fine.
  }
}

/** The path part of a link, for working out which item is current. */
const pathOf = (to: string) => to.split('?')[0]

const useEnquiryCount = () => {
  const [count, setCount] = useState(0)
  useEffect(() => {
    getEnquiryCount()
      .then((c: number) => setCount(c || 0))
      .catch(() => setCount(0))
  }, [])
  return count
}

const useNotesCount = () => {
  const [count, setCount] = useState(0)
  useEffect(() => {
    const load = () =>
      getNoteCount()
        .then((c: number) => setCount(c || 0))
        .catch(() => setCount(0))
    load()

    const onStorage = (e: StorageEvent) => {
      if (e.key === 'notesLastChanged') load()
    }
    window.addEventListener('notes:updated', load)
    window.addEventListener('storage', onStorage)
    return () => {
      window.removeEventListener('notes:updated', load)
      window.removeEventListener('storage', onStorage)
    }
  }, [])
  return count
}

const AppSidebar = ({
  allowedRoutes,
  collapsed,
  onCollapsedChange,
}: {
  allowedRoutes: string[]
  collapsed: boolean
  onCollapsedChange: (collapsed: boolean) => void
}) => {
  const navigate = useNavigate()
  const { pathname } = useLocation()

  const enquiryCount = useEnquiryCount()
  const notesCount = useNotesCount()

  const [feedbackOpen, setFeedbackOpen] = useState(false)
  const [query, setQuery] = useState('')
  const searchRef = useRef<HTMLInputElement>(null)

  // "/" jumps to the menu filter, unless already typing somewhere. (Ctrl/Cmd+K
  // belongs to the search in the header.)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      const typing =
        target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)
      if (e.key !== '/' || typing) return

      e.preventDefault()
      if (collapsed) onCollapsedChange(false)
      // After an expand the input is only there on the next paint.
      requestAnimationFrame(() => searchRef.current?.focus())
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [collapsed, onCollapsedChange])

  const groups: NavGroup[] = useMemo(() => {
    const allowed = Array.isArray(allowedRoutes) ? allowedRoutes : []
    const has = (...names: string[]) => names.some((n) => allowed.includes(n))
    const portal = has('Portal.Access')
    const admin = has('Portal.Administrator')

    // Note where a rule deliberately does or doesn't let administrators
    // through - it follows the API's policy for that area.
    const all: NavGroup[] = [
      {
        title: null,
        items: [
          { key: 'home', label: 'Home', icon: <FaHome />, to: '/', clearsFilters: true, visible: portal },
          { key: 'notes', label: 'Notes', icon: <FaRegStickyNote />, to: '/notes?status=incomplete', badge: notesCount, visible: portal },
          { key: 'diary', label: 'Diary', icon: <FaCalendarAlt />, to: '/diary', visible: has('Diary.View', 'Diary.Edit') && portal },
        ],
      },
      {
        title: 'Sales pipeline',
        items: [
          { key: 'enquiries', label: 'Enquiries', icon: <FaClipboardList />, to: '/enquiries', badge: enquiryCount, visible: has('Enquiries.View', 'Enquiries.Edit') && portal },
          { key: 'gonogo', label: 'Go/No-Go', icon: <FaCheckDouble />, to: '/go-no-go', visible: has('GoNoGo.View', 'GoNoGo.Edit') && portal },
          { key: 'quotes', label: 'Quotes', icon: <FaCalculator />, to: '/quotes', visible: has('Quotes.View', 'Quotes.Edit') && portal },
          { key: 'jobs', label: 'Jobs', icon: <FaBriefcase />, to: '/jobs', visible: has('Jobs.View', 'Jobs.Edit') && portal },
          { key: 'notifiable', label: 'Notifiable Works', icon: <FaClipboardCheck />, to: '/notifiable-works', visible: (has('NotifiableWorks.View') || admin) && portal },
        ],
      },
      {
        title: 'Finance',
        items: [
          { key: 'sales', label: 'Sales', icon: <FaFileInvoice />, to: '/invoicing', visible: has('Sales.View', 'Sales.Edit') && portal },
          { key: 'po', label: 'Purchase Orders', icon: <FaFileSignature />, to: '/purchase-orders', visible: (has('PurchaseOrders.View', 'PurchaseOrders.Edit') || admin) && portal },
          { key: 'pieces', label: 'Pieces', icon: <FaPuzzlePiece />, to: '/pieces', visible: has('Pieces.View', 'Pieces.Edit') && portal },
          { key: 'reports', label: 'Reports', icon: <FaChartBar />, to: '/reports', visible: has('Reports.Pipeline', 'Reports.CompletedJobs') && portal },
        ],
      },
      {
        title: 'Operations',
        items: [
          { key: 'reminders', label: 'Service Reminders', icon: <FaClock />, to: '/service-reminders', visible: has('ServiceReminders.View', 'ServiceReminders.Edit') && portal },
          { key: 'uniform', label: 'Uniform', icon: <FaTshirt />, to: '/uniform', visible: (has('Uniform.Manage') || admin) && portal },
          { key: 'contacts', label: 'Contacts', icon: <FaAddressBook />, to: '/contacts', visible: portal },
        ],
      },
      {
        title: 'Workspace',
        items: [
          { key: 'search', label: 'Global Search', icon: <FaSearch />, to: '/search', visible: portal },
          { key: 'intranet', label: 'Intranet', icon: <FaGlobe />, to: 'https://bamfords.sharepoint.com/sites/intranet', external: true, visible: portal },
          { key: 'feedback', label: 'Feedback', icon: <FaComment />, action: () => setFeedbackOpen(true), visible: portal },
          { key: 'management', label: 'Management', icon: <FaCogs />, to: '/management', visible: visibleManagementOptions(allowed).length > 0 && portal },
        ],
      },
    ]

    return all
      .map((g) => ({ ...g, items: g.items.filter((i) => i.visible) }))
      .filter((g) => g.items.length > 0)
  }, [allowedRoutes, enquiryCount, notesCount])

  const term = query.trim().toLowerCase()
  const shownGroups = term
    ? groups
        .map((g) => ({ ...g, items: g.items.filter((i) => i.label.toLowerCase().includes(term)) }))
        .filter((g) => g.items.length > 0)
    : groups

  const firstMatch = shownGroups[0]?.items[0]

  const isActive = (item: NavItem) => {
    if (!item.to || item.external) return false
    const path = pathOf(item.to)
    return pathname === path || pathname.startsWith(`${path}/`)
  }

  const go = (item: NavItem) => {
    if (item.clearsFilters) clearPersistedGridState()
    if (item.action) {
      item.action()
    } else if (item.external && item.to) {
      window.open(item.to, '_blank', 'noopener,noreferrer')
    } else if (item.to) {
      navigate(item.to)
    }
    setQuery('')
  }

  const toggle = () => {
    const next = !collapsed
    onCollapsedChange(next)
    writeCollapsed(next)
  }

  const renderItem = (item: NavItem) => {
    const content = (
      <>
        <span className='psb-item__icon' aria-hidden='true'>
          {item.icon}
          {collapsed && !!item.badge && <span className='psb-item__dot' />}
        </span>
        <span className='psb-item__label'>{item.label}</span>
        {!collapsed && !!item.badge && <span className='psb-item__badge'>{item.badge > 99 ? '99+' : item.badge}</span>}
        {!collapsed && item.external && <FaExternalLinkAlt className='psb-item__ext' aria-hidden='true' />}
      </>
    )

    const className = `psb-item${isActive(item) ? ' psb-item--active' : ''}`
    const tip = collapsed ? (item.badge ? `${item.label} (${item.badge})` : item.label) : undefined

    if (item.action) {
      return (
        <button type='button' className={className} onClick={() => go(item)} title={tip} aria-label={item.label}>
          {content}
        </button>
      )
    }

    if (item.external) {
      return (
        <a className={className} href={item.to} target='_blank' rel='noopener noreferrer' title={tip} aria-label={item.label}>
          {content}
        </a>
      )
    }

    return (
      <NavLink
        to={item.to!}
        className={className}
        onClick={() => {
          if (item.clearsFilters) clearPersistedGridState()
          setQuery('')
        }}
        title={tip}
        aria-label={item.label}
        aria-current={isActive(item) ? 'page' : undefined}
      >
        {content}
      </NavLink>
    )
  }

  return (
    <>
      <aside className={`psb${collapsed ? ' psb--collapsed' : ''}`} aria-label='Main navigation'>
        <div className='psb-head'>
          <img src={logoWhiteSVG} alt='Bamfords' className='psb-head__logo' />
          <button
            type='button'
            className='psb-head__toggle'
            onClick={toggle}
            aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
            title={collapsed ? 'Expand menu' : 'Collapse menu'}
          >
            <FaAngleDoubleLeft />
          </button>
        </div>

        {!collapsed && (
          <div className='psb-search'>
            <FaSearch className='psb-search__icon' aria-hidden='true' />
            <input
              ref={searchRef}
              className='psb-search__input'
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && firstMatch) go(firstMatch)
                if (e.key === 'Escape') {
                  setQuery('')
                  searchRef.current?.blur()
                }
              }}
              placeholder='Jump to…'
              aria-label='Filter the menu'
            />
            <kbd className='psb-search__kbd'>/</kbd>
          </div>
        )}

        <nav className='psb-nav'>
          {shownGroups.map((group) => (
            <div key={group.title ?? 'top'} className='psb-group'>
              {group.title && !collapsed && <p className='psb-group__title'>{group.title}</p>}
              {group.title && collapsed && <span className='psb-group__rule' aria-hidden='true' />}
              <ul className='psb-list'>
                {group.items.map((item) => (
                  <li key={item.key}>{renderItem(item)}</li>
                ))}
              </ul>
            </div>
          ))}
          {term && shownGroups.length === 0 && <p className='psb-empty'>Nothing matches “{query}”.</p>}
        </nav>

        {/* The signed-in user, preferences and log out live in the account
            menu at the top right - only the version is left down here. */}
        <div className='psb-foot' title={`Version ${packageJson.version}`}>
          v{packageJson.version}
        </div>
      </aside>

      {feedbackOpen && <Feedback onClose={() => setFeedbackOpen(false)} />}
    </>
  )
}

export { readCollapsed }
export default AppSidebar
